import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, User, Phone, Loader2, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { formatApiError } from '../lib/api';
import { validateField, sanitizeString, isSecureInput, errorMessages } from '../lib/validation';

const LOGO_URL = "/logo.png";

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [fieldErrors, setFieldErrors] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  
  const [touched, setTouched] = useState({
    name: false,
    email: false,
    phone: false,
    password: false,
    confirmPassword: false
  });

  const redirect = searchParams.get('redirect') || '/';

  const validateFieldOnChange = (field, value) => {
    let error = '';
    
    if (!isSecureInput(value)) {
      error = errorMessages.security;
    } else {
      switch (field) {
        case 'name':
          error = validateField(value, { required: true, name: true });
          break;
        case 'email':
          error = validateField(value, { required: true, email: true });
          break;
        case 'phone':
          error = validateField(value, { phone: true });
          break;
        case 'password':
          error = validateField(value, { required: true, minLength: 6 });
          break;
        case 'confirmPassword':
          if (!value) {
            error = errorMessages.required;
          } else if (value !== formData.password) {
            error = errorMessages.passwordMatch;
          }
          break;
        default:
          break;
      }
    }
    
    setFieldErrors(prev => ({ ...prev, [field]: error || '' }));
    return !error;
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateFieldOnChange(field, formData[field]);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (touched[field]) {
      validateFieldOnChange(field, value);
    }
    // Re-validate confirmPassword when password changes
    if (field === 'password' && touched.confirmPassword) {
      const confirmError = formData.confirmPassword && value !== formData.confirmPassword 
        ? errorMessages.passwordMatch : '';
      setFieldErrors(prev => ({ ...prev, confirmPassword: confirmError }));
    }
  };

  const validateForm = () => {
    const fields = ['name', 'email', 'phone', 'password', 'confirmPassword'];
    let isValid = true;
    
    fields.forEach(field => {
      const valid = validateFieldOnChange(field, formData[field]);
      if (!valid) isValid = false;
    });
    
    setTouched({
      name: true,
      email: true,
      phone: true,
      password: true,
      confirmPassword: true
    });
    
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await register({ 
        name: sanitizeString(formData.name), 
        email: sanitizeString(formData.email.toLowerCase()), 
        password: formData.password, 
        phone: formData.phone ? sanitizeString(formData.phone) : null 
      });
      navigate(redirect);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const renderField = (field, label, type, icon, placeholder, autoComplete) => {
    const Icon = icon;
    const value = formData[field];
    const hasError = touched[field] && fieldErrors[field];
    
    return (
      <div>
        <Label htmlFor={field} className="text-[#5C4033]">{label}</Label>
        <div className="relative mt-2">
          <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#C8962D]" />
          <Input
            id={field}
            type={field.includes('password') || field.includes('Password') ? (showPassword ? 'text' : 'password') : type}
            value={value}
            onChange={(e) => handleChange(field, e.target.value)}
            onBlur={() => handleBlur(field)}
            placeholder={placeholder}
            className={`pl-10 ${field.includes('assword') ? 'pr-10' : ''} ${hasError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
            data-testid={`register-${field}-input`}
            autoComplete={autoComplete}
          />
          {field === 'password' && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          )}
        </div>
        {hasError && (
          <p className="mt-1 text-sm text-red-600 flex items-center">
            <AlertCircle className="h-4 w-4 mr-1" />
            {fieldErrors[field]}
          </p>
        )}
      </div>
    );
  };

  return (
    <div data-testid="register-page" className="min-h-screen bg-[#FAF7F2] flex items-center justify-center py-12 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="text-center mb-8">
            <Link to="/">
              <img src={LOGO_URL} alt="La Finca" className="h-24 mx-auto mb-4" />
            </Link>
            <h1 className="font-serif text-2xl text-[#1A1A1A]">Crear Cuenta</h1>
            <p className="text-[#1A1A1A]/60 mt-2">Únete a la experiencia La Finca</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {renderField('name', 'Nombre completo *', 'text', User, 'Tu nombre', 'name')}
            {renderField('email', 'Correo electrónico *', 'email', Mail, 'tu@email.com', 'email')}
            {renderField('phone', 'Teléfono (opcional)', 'tel', Phone, '+52 311 123 4567', 'tel')}
            {renderField('password', 'Contraseña *', 'password', Lock, 'Mínimo 6 caracteres', 'new-password')}
            {renderField('confirmPassword', 'Confirmar contraseña *', 'password', Lock, 'Repite tu contraseña', 'new-password')}

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center">
                <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="register-submit-btn"
              className="w-full bg-[#5C4033] hover:bg-[#3D5A3E] text-white mt-2"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creando cuenta...
                </>
              ) : (
                'Crear Cuenta'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-[#1A1A1A]/60">
              ¿Ya tienes cuenta?{' '}
              <Link
                to={`/login${redirect !== '/' ? `?redirect=${redirect}` : ''}`}
                className="text-[#5C4033] hover:text-[#C8962D] font-medium"
              >
                Inicia sesión
              </Link>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
